import React, { useState } from "react";
import { collection, addDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase"; // adjust path if needed
import "./NewEmployeeForm.css";

const NewEmployeeForm = ({ onClose, onSubmit }) => {
  const [newEmployee, setNewEmployee] = useState({
    name: "",
    email: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    fuelCard: "",
    cdlNumber: "",
    cdlIssueDate: "",
    medCertNumber: "",
    medCertIssueDate: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setNewEmployee({
      ...newEmployee,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const employeesRef = collection(db, "employees");

      // Check if email already exists
      const emailCheck = await getDocs(
        query(employeesRef, where("email", "==", newEmployee.email.trim().toLowerCase()))
      );
      if (!emailCheck.empty) {
        setError("An employee with this email already exists.");
        setLoading(false);
        return;
      }

      // Check if phone already exists
      const phoneCheck = await getDocs(
        query(employeesRef, where("phone", "==", newEmployee.phone.trim()))
      );
      if (!phoneCheck.empty) {
        setError("An employee with this phone number already exists.");
        setLoading(false);
        return;
      }

      // All checks passed — save to Firebase
      const docRef = await addDoc(employeesRef, {
        name: newEmployee.name.trim(),
        email: newEmployee.email.trim().toLowerCase(),
        address: newEmployee.address.trim(),
        city: newEmployee.city.trim(),
        state: newEmployee.state.trim().toUpperCase(),
        zip: newEmployee.zip.trim(),
        phone: newEmployee.phone.trim(),
        fuelCard: newEmployee.fuelCard.trim() || null,
        cdlNumber: newEmployee.cdlNumber.trim() || null,
        cdlIssueDate: newEmployee.cdlIssueDate || null,
        medCertNumber: newEmployee.medCertNumber.trim() || null,
        medCertIssueDate: newEmployee.medCertIssueDate || null,
        createdAt: new Date(),
      });

      onSubmit({ id: docRef.id, ...newEmployee });
      onClose();

    } catch (err) {
      console.error("Error adding employee:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="new-employee-form">
      <div className="form-top">
        <div>
          <h2 className="form-title">New Employee</h2>
          <p className="form-subtitle">Fill in the details below to add a new employee.</p>
        </div>
        <button className="form-cancel-btn" onClick={onClose}>
          <i className="fas fa-times"></i> Cancel
        </button>
      </div>

      {error && (
        <div className="form-error">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      <form onSubmit={handleFormSubmit} className="form-body">

        {/* Row 1: Name + Email */}
        <div className="form-row">
          <div className="form-group">
            <label><i className="fas fa-user"></i> Name <span className="required">*</span></label>
            <input type="text" name="name" value={newEmployee.name} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label><i className="fas fa-envelope"></i> Email <span className="required">*</span></label>
            <input type="email" name="email" value={newEmployee.email} onChange={handleChange} required />
          </div>
        </div>

        {/* Row 2: Phone + Fuel Card */}
        <div className="form-row">
          <div className="form-group">
            <label><i className="fas fa-phone"></i> Phone <span className="required">*</span></label>
            <input type="text" name="phone" value={newEmployee.phone} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label><i className="fas fa-gas-pump"></i> Fuel Card</label>
            <input type="text" name="fuelCard" value={newEmployee.fuelCard} onChange={handleChange} />
          </div>
        </div>

        {/* Row 3: Address */}
        <div className="form-row">
          <div className="form-group full-width">
            <label><i className="fas fa-map-marker-alt"></i> Address</label>
            <input type="text" name="address" value={newEmployee.address} onChange={handleChange} />
          </div>
        </div>

        {/* Row 4: City + State + Zip */}
        <div className="form-row form-row-three">
          <div className="form-group">
            <label>City</label>
            <input type="text" name="city" value={newEmployee.city} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>State</label>
            <input type="text" name="state" value={newEmployee.state} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Zip Code</label>
            <input type="text" name="zip" value={newEmployee.zip} onChange={handleChange} />
          </div>
        </div>

        {/* ── CDL Section ───────────────────────────────── */}
        <div className="form-section-divider">
          <i className="fas fa-id-card"></i> CDL &amp; Certification
        </div>

        {/* Row 5: CDL # + CDL Issue Date */}
        <div className="form-row">
          <div className="form-group">
            <label><i className="fas fa-id-card"></i> CDL Driver's License #</label>
            <input type="text" name="cdlNumber" value={newEmployee.cdlNumber} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label><i className="fas fa-calendar-alt"></i> CDL Issue Date</label>
            <input type="date" name="cdlIssueDate" value={newEmployee.cdlIssueDate} onChange={handleChange} />
          </div>
        </div>

        {/* Row 6: Med. Cert. # + Med. Cert. Issue Date */}
        <div className="form-row">
          <div className="form-group">
            <label><i className="fas fa-file-medical"></i> Med Examiner's Cert Reg. #</label>
            <input type="text" name="medCertNumber" value={newEmployee.medCertNumber} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label><i className="fas fa-calendar-alt"></i> Med Examiner's Cert Issue Date</label>
            <input type="date" name="medCertIssueDate" value={newEmployee.medCertIssueDate} onChange={handleChange} />
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading
              ? <><i className="fas fa-spinner fa-spin"></i> Saving...</>
              : <><i className="fas fa-plus"></i> Add Employee</>
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewEmployeeForm;